function scrollIntoView(gallery, media, direction = 'horizontal') {
  switch (direction) {
    case 'horizontal':
      gallery.scrollTo({
        left: media.offsetLeft,
        behavior: 'smooth',
      });
      break;
    case 'vertical':
      gallery.scrollTo({
        top: media.offsetTop,
        behavior: 'smooth',
      });
      break;
    default:
      break;
  }
}

export default class FeaturedProductGallery extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.gallery = el.querySelector('[data-product-gallery-list]');
    this.galleryMedia = this.gallery.querySelectorAll('[data-media]');
    this.zoomableImageBtns = el.querySelectorAll('[data-product-gallery-media-zoom]');
    this.navigationThumbnails = el.querySelector('[data-product-gallery-navigation-thumbnails]');
    this.navigationCurrentMedia = el.querySelector('[data-product-gallery-navigation-current-media]');
    this.navigationArrowPrevious = el.querySelector('[data-product-gallery-navigation-arrow-previous]');
    this.navigationArrowNext = el.querySelector('[data-product-gallery-navigation-arrow-next]');

    this.state = {
      selectedMedia: this.data.selected_media,
    };

    if (this.data.enable_autoplay) {
      this._interval = setInterval(() => {
        const selectedMediaEl = this.gallery.querySelector(`[data-media='${this.state.selectedMedia}']`);
        const currMediaPosition = Array.prototype.indexOf.call(this.galleryMedia, selectedMediaEl);
        const nextMediaIndex = (currMediaPosition + 1) >= this.galleryMedia.length
          ? 0
          : currMediaPosition + 1;
        const matchingMedia = this.galleryMedia[nextMediaIndex];

        if (matchingMedia) {
          scrollIntoView(this.gallery, matchingMedia);
        }
      }, 3000);

      const event = this.events.register(el, 'pointerover', () => {
        clearInterval(this._interval);

        this.events.unregister(event);
      });
    }

    this.zoomableImageBtns.forEach(zoomableImageBtn => {
      this.events.register(zoomableImageBtn, 'click', e => {
        const img = e.target.closest('[data-media]');

        if (!img) return;

        const { media: mediaId } = img.dataset;
        const galleryModal = this.query('product-gallery-modal');

        if (galleryModal) {
          galleryModal.open(mediaId);
        }
      });
    });

    if (this.galleryMedia.length > 1) {
      this.events.register(this.navigationThumbnails, 'click', e => {
        e.preventDefault();

        const thumbnailMediaEl = e.target.closest('[data-media]');

        if (!thumbnailMediaEl) return; // We clicked into the void

        const matchingMedia = this.gallery.querySelector(`[data-media='${thumbnailMediaEl.dataset.media}']`);

        if (matchingMedia) {
          scrollIntoView(this.gallery, matchingMedia);
        }
      });

      this.events.register(this.navigationArrowPrevious, 'click', e => {
        e.preventDefault();

        const selectedMediaEl = this.gallery.querySelector(`[data-media='${this.state.selectedMedia}']`);
        const currMediaPosition = Array.prototype.indexOf.call(this.galleryMedia, selectedMediaEl);
        const nextMediaIndex = currMediaPosition - 1;
        const matchingMedia = this.galleryMedia[nextMediaIndex];

        if (matchingMedia) {
          scrollIntoView(this.gallery, matchingMedia);
        }
      });

      this.events.register(this.navigationArrowNext, 'click', e => {
        e.preventDefault();

        const selectedMediaEl = this.gallery.querySelector(`[data-media='${this.state.selectedMedia}']`);
        const currMediaPosition = Array.prototype.indexOf.call(this.galleryMedia, selectedMediaEl);
        const nextMediaIndex = currMediaPosition + 1;
        const matchingMedia = this.galleryMedia[nextMediaIndex];

        if (matchingMedia) {
          scrollIntoView(this.gallery, matchingMedia);
        }
      });

      this._intersectionObserver = new IntersectionObserver(this._handleIntersection.bind(this), { root: this.gallery, rootMargin: '0px 0px 0px 0px', threshold: 0.5 });
      this.galleryMedia.forEach(galleryMedia => this._intersectionObserver.observe(galleryMedia));

      Core.Breakpoints.onChange(this._handleBreakpointChange.bind(this));
    }
  }

  onContextChange({ selectedVariant }) {
    if (!selectedVariant || !selectedVariant.featured_media) return;

    const matchingMedia = this.gallery.querySelector(`[data-media='${selectedVariant.featured_media.id}']`);

    if (matchingMedia) {
      clearInterval(this._interval);
      scrollIntoView(this.gallery, matchingMedia);

      this.setState({
        selectedMedia: matchingMedia.dataset.media,
      });
    }
  }

  onStateChange(newState) {
    const matchingMedia = this.gallery.querySelector(`[data-media='${newState.selectedMedia}']`);
    const mediaThumbnails = this.navigationThumbnails.querySelectorAll('[data-media]');
    const matchingMediaThumbnail = this.navigationThumbnails.querySelector(`[data-media='${newState.selectedMedia}']`);

    mediaThumbnails.forEach(mediaThumbnail => mediaThumbnail.removeAttribute('data-media-selected'));

    if (matchingMediaThumbnail) {
      scrollIntoView(this.navigationThumbnails, matchingMediaThumbnail, 'vertical');

      matchingMediaThumbnail.setAttribute('data-media-selected', '');
    }

    const shopifyXrEl = this.el.querySelector('[data-gallery-xr]');

    if (!shopifyXrEl) return;

    if (matchingMedia.dataset.mediaType === 'model') {
      shopifyXrEl.dataset.shopifyModel3dId = newState.selectedMedia;
    } else {
      shopifyXrEl.dataset.shopifyModel3dId = shopifyXrEl.dataset.defaultModelId;
    }
  }

  /**
   * Called when the component is dismounted
   */
  onDismount() {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }

    this.events.unregisterAll();
  }

  _handleBreakpointChange({ current }) {
    if (current.value === 'S') {
      const matchingMedia = this.galleryMedia[0];

      if (matchingMedia) {
        scrollIntoView(this.gallery, matchingMedia);
        const matchingMediaIndex = Array.prototype
          .indexOf.call(this.galleryMedia, matchingMedia);
        this.navigationArrowPrevious.disabled = matchingMediaIndex === 0;
        this.navigationCurrentMedia.innerText = matchingMediaIndex + 1;
        this.navigationArrowNext.disabled = matchingMediaIndex === this.galleryMedia.length - 1;
      }
    }
  }

  /**
   * Handles the intersection event of a media item and gallery viewport
   * Updates the navigation arrows
   */
  _handleIntersection(entries) {
    const intersectingEntry = entries
      .reduce((acc, entry) => {
        let largestIntersectingEntry = acc;

        if (
          entry.intersectionRatio >= this._intersectionObserver.thresholds
          && (
            !largestIntersectingEntry
            || largestIntersectingEntry.intersectionRatio < entry.intersectionRatio
          )
        ) {
          largestIntersectingEntry = entry;
        }

        return largestIntersectingEntry;
      }, null);

    if (!intersectingEntry) return;

    Core.Media.pauseAll();

    const intersectingMediaIndex = Array.prototype
      .indexOf.call(this.galleryMedia, intersectingEntry.target);

    if (intersectingMediaIndex >= 0) {
      const currImgPosition = intersectingMediaIndex + 1; // Position is 1-based index

      this.navigationArrowPrevious.disabled = intersectingMediaIndex === 0;
      this.navigationCurrentMedia.innerText = currImgPosition;
      this.navigationArrowNext.disabled = intersectingMediaIndex === this.galleryMedia.length - 1;

      this.setState({
        selectedMedia: intersectingEntry.target.dataset.media,
      });
    }
  }
}
